'use client';

import { ChangeEvent, Fragment, HTMLAttributes, Suspense, useMemo, useRef, useState } from 'react';

import { Apps } from '@/types';
import { Text } from '@/ui';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { distanceBetweenTwoCoordinates } from '@lib/server';
import { IconChevronCompactUp } from '@tabler/icons-react';
import clsx from 'clsx';
import { ViewState } from 'react-map-gl/mapbox';
import { useMediaQuery } from 'usehooks-ts';

import { useGlobal } from '@/lib/global-store';

import { Drawer, DrawerContent, DrawerTrigger } from '../../layout/drawer';
import { MapProps, Maps } from '../map';
import { VenueCard, VenueCardProps } from '../venue-card';
import { PlacesSearchBar } from './places-search-bar';
import { sortPlaces } from './sort-places';

export interface PlacesSearchBarProps extends React.ComponentProps<'input'> {
  view: 'grid' | 'map';
  onViewChange: (view: 'grid' | 'map') => void;
  value: string;
  onChange: (value: ChangeEvent<HTMLInputElement>) => void;
}

export type SearchResults = {
  geometry: { coordinates: string };
  place_name: string;
};

export type PlaceSearchPlace = VenueCardProps & { slug?: string; apps?: Apps };

export interface PlacesSearchProps extends HTMLAttributes<HTMLDivElement> {
  places: Array<PlaceSearchPlace>;
  mapStyle?: MapProps['style'];
  accessToken: string;
}

export const PlacesSearch = ({ places, mapStyle, accessToken, id, className }: PlacesSearchProps) => {
  const [view, setView] = useState<'grid' | 'map'>('grid');
  const [sortedPlaces, setSortedPlaces] = useState<PlaceSearchPlace[]>(
    places.sort((a, b) => a.title.localeCompare(b.title)),
  );
  const [searchResults, setSearchResults] = useState<SearchResults[]>([]);
  const [searchLocation, setSearchLocation] = useState<number[] | null>(null);
  const [parent] = useAutoAnimate();
  const [open, setOpen] = useState(false);
  const mobile = useMediaQuery('(max-width: 640px)');
  const containerRef = useRef<HTMLDivElement>(null);
  const { setOpenBook, setDmnConfig } = useGlobal();

  const [overrideViewState, setOverrideViewState] = useState<ViewState>();

  const handleVenueCardClick = (latitude: number, longitude: number) => {
    if (mobile) {
      setOpen(false);
    }
    setSortedPlaces(sortPlaces(latitude, longitude, places));
    setOverrideViewState({
      latitude,
      longitude,
      zoom: 14,
    } as ViewState);
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePinClick = (lat: number, long: number) => {
    if (mobile) {
      setOpen(true);
    }
    setSortedPlaces(sortPlaces(lat, long, places));
    setOverrideViewState({
      latitude: lat,
      longitude: long,
      zoom: 14,
    } as ViewState);
  };

  const MapViewVenueCards = useMemo(
    () =>
      sortedPlaces.map((place, index) => (
        <Fragment key={`map_${place.title}_${place.summary}`}>
          <VenueCard
            {...place}
            summary={`${place.address.addressLine1}, ${place.address.cityOrTown}, ${place.address.postcode}`}
            href={undefined}
            className="shadow-lg"
            image={index === 0 ? place.image : undefined}
            pills={
              index === 0
                ? {
                    distance: searchLocation
                      ? `${distanceBetweenTwoCoordinates(
                          searchLocation[0],
                          searchLocation[1],
                          place.geo.latitude,
                          place.geo.longitude,
                        ).toFixed(1)} mi`
                      : undefined,
                    location: place.address.addressLine1,
                  }
                : {}
            }
            actions={{
              primary: {
                children: 'Book Now',
                onClick: () => {
                  setOpenBook(true);
                  setDmnConfig({
                    venue: place.apps?.designMyNight?.id,
                  });
                },
              },
              secondary: {
                variant: 'outline',
                children: 'Explore',
                href: `${place.href}`,
              },
            }}
            onClick={() => handleVenueCardClick(place.geo.latitude, place.geo.longitude)}
          />
        </Fragment>
      )),
    [handleVenueCardClick, searchLocation, setDmnConfig, setOpenBook, sortedPlaces],
  );

  return (
    <section
      id={id}
      className={clsx(
        {
          'h-[75svh]': view === 'map',
        },
        '@container relative z-10 flex w-full max-w-(--breakpoint-2xl) rounded-sm',
        className,
      )}
    >
      <div
        className={clsx('flex h-full w-full flex-col px-4 transition-all duration-300 ease-out sm:px-8', {
          'items-center gap-8': view === 'grid',
          'items-start gap-3 py-0': view === 'map',
        })}
      >
        {accessToken && (
          <PlacesSearchBar
            view={view}
            accessToken={accessToken}
            handlePinClick={handlePinClick}
            onViewChange={setView}
            setOverrideViewState={setOverrideViewState}
            places={places}
            setSearchLocation={setSearchLocation}
            searchResults={searchResults}
            setSortedPlaces={setSortedPlaces}
            setSearchResults={setSearchResults}
          />
        )}

        {!sortedPlaces?.length && (
          <div className="flex w-full flex-col items-center gap-1">
            <Text element={'h4'}>Results</Text>
            <Text element={'p'} className="text-muted-foreground">
              No results found
            </Text>
          </div>
        )}

        {sortedPlaces?.length && view === 'grid' && (
          <div
            ref={parent}
            className="mt-4 grid w-full grid-cols-1 gap-4 sm:mt-0 sm:gap-8 @2xl:grid-cols-2 @5xl:grid-cols-3 @7xl:grid-cols-4"
          >
            {sortedPlaces.map((place) => (
              <VenueCard
                {...place}
                key={`grid_${place.title}_${place.summary}`}
                pills={{
                  distance: searchLocation
                    ? `${distanceBetweenTwoCoordinates(
                        searchLocation[0],
                        searchLocation[1],
                        place.geo.latitude,
                        place.geo.longitude,
                      ).toFixed(1)} mi`
                    : undefined,
                  location: place.address.addressLine1,
                }}
                actions={{
                  primary: {
                    children: 'Book Now',
                    onClick: () => {
                      setOpenBook(true);
                      setDmnConfig({
                        venue: place.apps?.designMyNight?.id,
                      });
                    },
                  },
                  secondary: {
                    variant: 'outline',
                    children: 'Explore',
                    href: `${place.href}`,
                  },
                }}
              />
            ))}
          </div>
        )}

        {sortedPlaces?.length && view === 'map' && !mobile && (
          <div ref={containerRef} className="z-10 flex h-[75svh] w-full max-w-sm flex-col gap-3 overflow-y-auto py-6">
            {MapViewVenueCards}
          </div>
        )}
      </div>

      {sortedPlaces?.length && view === 'map' && mobile && (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger className="bg-background border-border text-muted absolute bottom-0 z-20 flex h-10 w-full items-center justify-center overflow-hidden rounded border-t">
            <IconChevronCompactUp size={48} stroke={1} />
          </DrawerTrigger>
          <DrawerContent>
            <div className="flex max-h-[80svh] w-full flex-col gap-3 overflow-y-auto p-4 pt-8">{MapViewVenueCards}</div>
          </DrawerContent>
        </Drawer>
      )}

      {sortedPlaces?.length && view === 'map' && (
        <div className="absolute z-0 h-full w-full">
          <Suspense>
            <Maps
              accessToken={accessToken}
              style={mapStyle}
              locations={sortedPlaces.map(({ geo, title }, i) => ({
                ...geo,
                name: title,
                primary: i === 0,
              }))}
              overrideViewState={overrideViewState}
              onPinClick={handlePinClick}
            />
          </Suspense>
        </div>
      )}
    </section>
  );
};
